import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

import { Body } from './typography';

const vTextareaVariants = cva(
  'min-h-24 rounded-lg border bg-card px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-input focus:border-primary-600',
        error: 'border-error-600 focus:border-error-600',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type VTextareaProps = React.ComponentPropsWithoutRef<'textarea'> &
  VariantProps<typeof vTextareaVariants> & {
    label?: string;
    error?: string;
  };

export const VTextarea = React.forwardRef<HTMLTextAreaElement, VTextareaProps>(
  function VTextarea({ className, variant, label, error, id, ...props }, ref) {
    const generatedId = React.useId();
    const textareaId = id ?? generatedId;
    const resolvedVariant = error ? 'error' : variant;

    const textarea = (
      <textarea
        ref={ref}
        id={textareaId}
        aria-invalid={error ? true : undefined}
        className={cn(vTextareaVariants({ variant: resolvedVariant, className }))}
        {...props}
      />
    );

    if (!label && !error) return textarea;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <Body as="label" htmlFor={textareaId} weight="medium">
            {label}
          </Body>
        )}
        {textarea}
        {error && (
          <Body as="span" tone="error">
            {error}
          </Body>
        )}
      </div>
    );
  },
);
VTextarea.displayName = 'VTextarea';

export { vTextareaVariants };
export type { VTextareaProps };
